import { AlertCircle, Clock, Eye, FileEdit, XCircle } from 'lucide-react';

import type { TFunction } from 'i18next';

import { CONSIGNMENT_STATUS, type ConsignmentTableData } from '@/entities/consignments';
import type { useConsignmentActions } from '@/features/consignment-actions';
import type { TableAction } from '@/shared/ui/data-table';

type ConsignmentActionHandlers = ReturnType<typeof useConsignmentActions>['actions'];

interface BuildConsignmentRowActionsArgs {
  actions: ConsignmentActionHandlers;
  t: TFunction;
}

/**
 * Builds the per-row action list for the consignments table.
 *
 * The Cancel action is removed (`isHidden`) for consignments already in the
 * cancelled (`CA`) status — the single-row counterpart to filtering
 * already-cancelled consignments out of the bulk cancel. Every other action
 * applies to all rows.
 */
export function buildConsignmentRowActions({
  actions,
  t
}: BuildConsignmentRowActionsArgs): TableAction<ConsignmentTableData>[] {
  return [
    {
      id: 'show-disposition',
      // listaction.RQ.DIS does not exist in any locale (raw key rendered);
      // cmrlist.showdisposition.label is the translated legacy JSF label.
      label: t('cmrlist.showdisposition.label', 'Show Disposition'),
      icon: Eye,
      onClick: (row) => actions.showDisposition([row.id])
    },
    {
      id: 'edit',
      label: t('listaction.RQ.EDT'),
      icon: FileEdit,
      onClick: (row) => actions.editView([row.id])
    },
    {
      id: 'priority',
      label: t('listaction.RQ.PRT'),
      icon: AlertCircle,
      onClick: (row) => actions.changePriority([row.id])
    },
    {
      id: 'hold',
      label: t('listaction.RQ.HOL'),
      icon: Clock,
      onClick: (row) => actions.placeOnHold([row.id])
    },
    {
      id: 'cancel',
      label: t('listaction.RQ.CAN'),
      icon: XCircle,
      isHidden: (row) => row.statusCode === CONSIGNMENT_STATUS.CA,
      onClick: (row) => actions.cancel([row.id])
    }
  ];
}
